import { _ as __vitePreload } from "main/preload-helper";
import { m as membershipService, F as FORTHEM_TIER_NAME_ALLACCESS, a as FORTHEM_TIER_NAME_SUPPORT } from "main/membership-service";
class MembershipCartInterceptor {
  constructor() {
    this.isIntercepting = false;
    this.originalFetch = window.fetch;
    this.init();
  }
  init() {
    this.setupCartInterceptor();
  }
  setupCartInterceptor() {
    if (this.isIntercepting) {
      console.warn("[MembershipInterceptor] Already intercepting, skipping setup");
      return;
    }
    this.isIntercepting = true;
    const originalFetch = this.originalFetch;
    window.fetch = async (...args) => {
      var _a;
      const [input, init] = args;
      const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      if (url.includes("/cart/add") && ((_a = init == null ? void 0 : init.method) == null ? void 0 : _a.toLowerCase()) === "post") {
        try {
          const body = init.body;
          if (body) {
            const bodyToValidate = this.cloneBodyIfNeeded(body);
            const validationResult = await this.validateCartAddRequest(bodyToValidate);
            if (!validationResult.allowed) {
              console.warn("[MembershipInterceptor] Request blocked:", validationResult.reason);
              this.showErrorFeedback(validationResult.reason, validationResult.messageType);
              window.dispatchEvent(new CustomEvent("membership:cart-add:blocked", {
                detail: {
                  reason: validationResult.reason,
                  messageType: validationResult.messageType,
                  sellingPlanId: validationResult.sellingPlanId
                }
              }));
              return new Response(
                JSON.stringify({
                  status: 422,
                  description: validationResult.reason,
                  message: validationResult.reason
                }),
                {
                  status: 422,
                  statusText: "Unprocessable Entity",
                  headers: {
                    "Content-Type": "application/json"
                  }
                }
              );
            }
          }
        } catch (error) {
          console.error("[MembershipInterceptor] Error validating cart add request:", error);
        }
      }
      return originalFetch.apply(window, args);
    };
  }
  async validateCartAddRequest(body) {
    try {
      const requestData = await this.parseRequestBody(body);
      if (!requestData) {
        return { allowed: true, reason: "", messageType: "cart-change" };
      }
      const items = this.extractItems(requestData);
      if (!items || items.length === 0) {
        return { allowed: true, reason: "", messageType: "cart-change" };
      }
      const { membershipTierValidator } = await __vitePreload(async () => {
        const { membershipTierValidator: membershipTierValidator2 } = await import("main/membership-tier-validator");
        return { membershipTierValidator: membershipTierValidator2 };
      }, true ? [] : void 0, import.meta.url);
      for (const item of items) {
        const sellingPlanId = this.extractSellingPlanId(item);
        if (!sellingPlanId) {
          continue;
        }
        const sellingPlanIdNum = parseInt(sellingPlanId.toString());
        if (!membershipTierValidator.isMembershipProduct(sellingPlanIdNum)) {
          continue;
        }
        const cart = await membershipTierValidator.getCurrentCart();
        const existingMembershipItems = cart.items.filter(
          (cartItem) => cartItem.selling_plan_allocation && membershipTierValidator.isMembershipProduct(cartItem.selling_plan_allocation.selling_plan.id)
        );
        if (existingMembershipItems.length > 0) {
          const existingTier = existingMembershipItems[0].selling_plan_allocation.selling_plan.id;
          const existingTierPriority = membershipTierValidator.getTierPriority(existingTier);
          const requestedTierPriority = membershipTierValidator.getTierPriority(sellingPlanIdNum);
          console.log("[MembershipInterceptor] Existing membership priority:", existingTierPriority, "Requested priority:", requestedTierPriority);
          if (requestedTierPriority < existingTierPriority) {
            console.log("[MembershipInterceptor] Higher priority membership requested, will remove lower priority");
            await this.removeMembershipFromCart(existingMembershipItems[0].key);
            return {
              allowed: true,
              reason: "",
              messageType: "cart-change",
              shouldRemoveLowerPriority: true
            };
          } else if (requestedTierPriority === existingTierPriority) {
            return {
              allowed: false,
              reason: "Change the membership level in your cart",
              messageType: "cart-change",
              sellingPlanId: sellingPlanIdNum
            };
          } else {
            return {
              allowed: false,
              reason: "Change the membership level in your cart",
              messageType: "cart-change",
              sellingPlanId: sellingPlanIdNum
            };
          }
        }
        const validation = await membershipTierValidator.validateMembershipAddToCart(sellingPlanIdNum);
        if (!validation.canAddToCart) {
          return {
            allowed: false,
            reason: validation.reason,
            messageType: validation.messageType,
            sellingPlanId: sellingPlanIdNum
          };
        }
      }
      return { allowed: true, reason: "", messageType: "cart-change" };
    } catch (error) {
      console.error("[MembershipInterceptor] Error validating request:", error);
      return { allowed: true, reason: "", messageType: "cart-change" };
    }
  }
  async removeMembershipFromCart(itemKey) {
    try {
      console.log("[MembershipInterceptor] Removing lower priority membership from cart:", itemKey);
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });
      if (response.ok) {
        console.log("[MembershipInterceptor] Successfully removed lower priority membership");
        window.dispatchEvent(new CustomEvent("theme:cart:change"));
      } else {
        console.error("[MembershipInterceptor] Failed to remove membership:", await response.text());
      }
    } catch (error) {
      console.error("[MembershipInterceptor] Error removing membership from cart:", error);
    }
  }
  cloneBodyIfNeeded(body) {
    if (body instanceof FormData) {
      const cloned = new FormData();
      Array.from(body.entries()).forEach(([key, value]) => {
        cloned.append(key, value);
      });
      return cloned;
    }
    if (body instanceof URLSearchParams) {
      return new URLSearchParams(body);
    }
    return body;
  }
  async parseRequestBody(body) {
    try {
      if (typeof body === "string") {
        try {
          return JSON.parse(body);
        } catch {
          const params = new URLSearchParams(body);
          return Object.fromEntries(params);
        }
      } else if (body instanceof FormData) {
        return Object.fromEntries(body);
      } else if (body instanceof URLSearchParams) {
        return Object.fromEntries(body);
      } else if (body instanceof ReadableStream) {
        console.warn("[MembershipInterceptor] ReadableStream body not supported, allowing request");
        return null;
      }
      return null;
    } catch (error) {
      console.error("[MembershipInterceptor] Error parsing request body:", error);
      return null;
    }
  }
  extractItems(requestData) {
    if (requestData.items) {
      return Array.isArray(requestData.items) ? requestData.items : [requestData.items];
    }
    const nestedItems = [];
    let index = 0;
    while (true) {
      const itemId = requestData[`items[${index}][id]`] || requestData[`items[${index}][variant_id]`];
      if (!itemId) break;
      const item = {
        id: itemId,
        quantity: requestData[`items[${index}][quantity]`] || 1,
        selling_plan: requestData[`items[${index}][selling_plan]`] || requestData[`items[${index}][selling_plan_id]`]
      };
      nestedItems.push(item);
      index++;
    }
    if (nestedItems.length > 0) {
      return nestedItems;
    }
    if (requestData.id || requestData.variant_id) {
      return [{
        id: requestData.id || requestData.variant_id,
        quantity: requestData.quantity || 1,
        selling_plan: requestData.selling_plan || requestData.selling_plan_id
      }];
    }
    return [];
  }
  extractSellingPlanId(item) {
    return item.selling_plan || item.selling_plan_id || null;
  }
  showErrorFeedback(message, messageType) {
    var _a;
    const notification = document.createElement("div");
    notification.className = `
      membership-cart-error-notification
      rs-fixed rs-top-4 rs-right-4 rs-z-50 rs-max-w-md rs-p-4 rs-rounded-lg rs-shadow-lg
      ${messageType === "cart-change" ? "rs-bg-orange-50 rs-border rs-border-orange-200 rs-text-orange-800" : "rs-bg-red-50 rs-border rs-border-red-200 rs-text-red-800"}
    `;
    notification.innerHTML = `
      <div class="rs-flex rs-items-start rs-space-x-3">
        <div class="rs-flex-shrink-0">
          <svg class="rs-h-5 rs-w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="rs-flex-1">
          <p class="rs-font-medium">${this.escapeHtml(message)}</p>
          ${messageType === "account-upgrade" ? '<p class="rs-mt-1 rs-text-sm rs-opacity-90">Visit your account to manage your current membership.</p>' : ""}
        </div>
        <button 
          class="rs-flex-shrink-0 rs-text-gray-400 hover:rs-text-gray-600"
          onclick="this.parentElement.parentElement.remove()"
        >
          <svg class="rs-h-5 rs-w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5e3);
    (_a = notification.querySelector("button")) == null ? void 0 : _a.addEventListener("click", () => {
      notification.remove();
    });
  }
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
const membershipCartInterceptor = new MembershipCartInterceptor();
if (typeof window !== "undefined") {
  window.membershipCartInterceptor = membershipCartInterceptor;
}
const _SubscriptionGiftsHandler = class _SubscriptionGiftsHandler {
  constructor() {
    this.isAddingGifts = false;
    this.metafieldCache = /* @__PURE__ */ new Map();
    this.init();
  }
  /**
   * Helper method to dispatch the subscription added event
   * Call this when a subscription product is added to cart to trigger gift addition
   */
  static dispatchSubscriptionAdded(productHandle, productId, sellingPlanName) {
    document.dispatchEvent(new CustomEvent("theme:subscription:added", {
      bubbles: true,
      detail: {
        handle: productHandle,
        product_id: productId,
        selling_plan_name: sellingPlanName
      }
    }));
  }
  init() {
    this.setupCartInterceptor();
    document.addEventListener("theme:subscription:added", this.handleSubscriptionAdded.bind(this));
    window.addEventListener("beforeunload", () => this.clearCaches());
    document.addEventListener("theme:cart:refresh", (event) => {
      var _a;
      const cart = (_a = event.detail) == null ? void 0 : _a.cart;
      if (cart && cart.item_count === 0) {
        this.clearCaches();
      }
    });
  }
  clearCaches() {
    this.metafieldCache.clear();
  }
  setupCartInterceptor() {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      var _a;
      const [input, init] = args;
      const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      if (url.includes("/cart/add") && ((_a = init == null ? void 0 : init.method) == null ? void 0 : _a.toLowerCase()) === "post") {
        try {
          const body = init.body;
          if (body) {
            this.analyzeCartAddRequest(body).catch((error) => {
              console.error("🎁 SubscriptionGifts: Error analyzing cart add request:", error);
            });
          }
        } catch (error) {
          console.error("🎁 SubscriptionGifts: Error setting up cart add analysis:", error);
        }
      }
      return originalFetch.apply(window, args);
    };
  }
  async analyzeCartAddRequest(body) {
    try {
      let requestData = null;
      if (typeof body === "string") {
        try {
          requestData = JSON.parse(body);
        } catch {
          const params = new URLSearchParams(body);
          requestData = Object.fromEntries(params);
        }
      } else if (body instanceof FormData) {
        requestData = Object.fromEntries(body);
      } else if (body instanceof URLSearchParams) {
        requestData = Object.fromEntries(body);
      }
      if (!requestData) return;
      await this.checkForSubscriptionProducts(requestData);
    } catch (error) {
      console.error("🎁 SubscriptionGifts: Error parsing cart add request:", error);
    }
  }
  async checkForSubscriptionProducts(requestData) {
    try {
      const items = requestData.items || [requestData];
      for (const item of Array.isArray(items) ? items : [items]) {
        const sellingPlanId = item.selling_plan || item.selling_plan_id;
        const variantId = item.id || item.variant_id;
        if (sellingPlanId && variantId) {
          await this.processSubscriptionFromVariant(variantId, sellingPlanId);
        }
      }
    } catch (error) {
      console.error("🎁 SubscriptionGifts: Error checking for subscription products:", error);
    }
  }
  async processSubscriptionFromVariant(variantId, sellingPlanId) {
    try {
      const details = await this.getVariantAndSellingPlanDetails(variantId, sellingPlanId);
      if (!details) {
        return;
      }
      const cartItem = {
        handle: details.product.handle,
        product_id: this.extractProductIdFromGid(details.product.id),
        variant_id: variantId,
        selling_plan_allocation: {
          selling_plan: {
            id: sellingPlanId,
            name: details.sellingPlan.name
          }
        }
      };
      const cart = await this.getCartData();
      await this.processSubscriptionItem(cartItem, cart);
    } catch (error) {
      console.error("🎁 SubscriptionGifts: Error processing subscription from variant:", error);
      if (error instanceof Error) {
        console.error("🎁 SubscriptionGifts: Error stack:", error.stack);
      }
    }
  }
  async getVariantAndSellingPlanDetails(variantId, sellingPlanId) {
    var _a;
    try {
      const variantGid = `gid://shopify/ProductVariant/${variantId}`;
      const query = `
        query GetVariantDetails($variantId: ID!) {
          node(id: $variantId) {
            ... on ProductVariant {
              id
              product {
                id
                handle
                title
                sellingPlanGroups(first: 10) {
                  nodes {
                    sellingPlans(first: 10) {
                      nodes {
                        id
                        name
                        description
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;
      const response = await fetch(_SubscriptionGiftsHandler.STOREFRONT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": _SubscriptionGiftsHandler.ACCESS_TOKEN
        },
        body: JSON.stringify({
          query,
          variables: {
            variantId: variantGid
          }
        })
      });
      if (!response.ok) {
        throw new Error(`Storefront API request failed: ${response.status}`);
      }
      const data = await response.json();
      if (data.errors) {
        console.error("🎁 SubscriptionGifts: Storefront API errors:", data.errors);
        return null;
      }
      const variant = (_a = data.data) == null ? void 0 : _a.node;
      if (!variant || !variant.product) {
        console.error("🎁 SubscriptionGifts: Missing variant data from API");
        return null;
      }
      let foundSellingPlan = null;
      const sellingPlanIdToFind = sellingPlanId.toString();
      for (const group of variant.product.sellingPlanGroups.nodes) {
        for (const plan of group.sellingPlans.nodes) {
          const planId = this.extractProductIdFromGid(plan.id);
          if (planId === sellingPlanIdToFind) {
            foundSellingPlan = plan;
            break;
          }
        }
        if (foundSellingPlan) break;
      }
      if (!foundSellingPlan) {
        console.error("🎁 SubscriptionGifts: Could not find selling plan with ID:", sellingPlanId);
        return null;
      }
      return {
        product: variant.product,
        sellingPlan: foundSellingPlan
      };
    } catch (error) {
      console.error("🎁 SubscriptionGifts: Error fetching variant and selling plan details:", error);
      return null;
    }
  }
  async handleSubscriptionAdded(event) {
    if (this.isAddingGifts) {
      return;
    }
    try {
      const eventDetail = event.detail || event;
      const productHandle = eventDetail.handle;
      const productId = eventDetail.product_id || eventDetail.productId;
      const sellingPlanName = eventDetail.selling_plan_name || eventDetail.sellingPlanName;
      if (!productHandle || !productId || !sellingPlanName) {
        console.error("🎁 SubscriptionGifts: Missing required subscription data from event", {
          productHandle,
          productId,
          sellingPlanName
        });
        return;
      }
      const subscriptionItem = {
        handle: productHandle,
        product_id: productId,
        selling_plan_allocation: {
          selling_plan: {
            name: sellingPlanName
          }
        }
      };
      const cart = await this.getCartData();
      await this.processSubscriptionItem(subscriptionItem, cart);
    } catch (error) {
      console.error("🎁 SubscriptionGifts: Error handling subscription added:", error);
    }
  }
  async processSubscriptionItem(item, cart) {
    var _a, _b;
    try {
      const productHandle = item.handle;
      const sellingPlanName = (_b = (_a = item.selling_plan_allocation) == null ? void 0 : _a.selling_plan) == null ? void 0 : _b.name;
      if (!productHandle || !sellingPlanName) {
        return;
      }
      const subscriptionCacheKey = `${item.product_id}:${sellingPlanName}:${Date.now()}`;
      const frequency = this.getFrequencyFromSellingPlan(sellingPlanName);
      if (!frequency) {
        return;
      }
      const metafieldKey = `subscription.${frequency}_gifts`;
      const giftProductIds = await this.getGiftProductIds(productHandle, metafieldKey);
      if (!giftProductIds || giftProductIds.length === 0) {
        return;
      }
      await this.addGiftProductsToCart(giftProductIds, cart, item.product_id, subscriptionCacheKey);
    } catch (error) {
      console.error("SubscriptionGifts: Error processing subscription item:", error);
    }
  }
  getFrequencyFromSellingPlan(sellingPlanName) {
    const name = sellingPlanName.toLowerCase();
    if (name.includes("annual") || name.includes("yearly") || name.includes("year")) {
      return "annual";
    }
    if (name.includes("quarterly") || name.includes("quarter")) {
      return "quarterly";
    }
    if (name.includes("monthly") || name.includes("month")) {
      return "monthly";
    }
    return null;
  }
  async getGiftProductIds(productHandle, metafieldKey) {
    var _a, _b, _c, _d;
    try {
      const cacheKey = `${productHandle}:${metafieldKey}`;
      if (this.metafieldCache.has(cacheKey)) {
        return this.metafieldCache.get(cacheKey);
      }
      const query = `
        query GetProductMetafields($handle: String!) {
          product(handle: $handle) {
            title
            handle
            metafield(namespace: "subscription", key: "${metafieldKey.split(".")[1]}") {
              references(first: 10) {
                nodes {
                  ... on Product {
                    id
                  }
                }
              }
            }
          }
        }
      `;
      const response = await fetch(_SubscriptionGiftsHandler.STOREFRONT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": _SubscriptionGiftsHandler.ACCESS_TOKEN
        },
        body: JSON.stringify({
          query,
          variables: { handle: productHandle }
        })
      });
      if (!response.ok) {
        throw new Error(`Storefront API request failed: ${response.status}`);
      }
      const data = await response.json();
      if (data.errors) {
        console.error("SubscriptionGifts: Storefront API errors:", data.errors);
        return [];
      }
      const references = ((_d = (_c = (_b = (_a = data.data) == null ? void 0 : _a.product) == null ? void 0 : _b.metafield) == null ? void 0 : _c.references) == null ? void 0 : _d.nodes) || [];
      const productIds = references.map((ref) => ref.id);
      this.metafieldCache.set(cacheKey, productIds);
      return productIds;
    } catch (error) {
      console.error("SubscriptionGifts: Error fetching gift product IDs:", error);
      return [];
    }
  }
  async addGiftProductsToCart(giftProductIds, cart, subscriptionProductId, subscriptionCacheKey) {
    try {
      if (giftProductIds.length === 0) return;
      this.isAddingGifts = true;
      const giftProducts = await this.getGiftProductsDetails(giftProductIds);
      if (giftProducts.length === 0) {
        this.isAddingGifts = false;
        return;
      }
      const availableGifts = giftProducts.filter(
        (product) => product.availableForSale && product.totalInventory > 0
      );
      if (availableGifts.length === 0) {
        this.isAddingGifts = false;
        return;
      }
      const itemsToAdd = await this.getGiftProductVariants(availableGifts);
      if (itemsToAdd.length === 0) {
        this.isAddingGifts = false;
        return;
      }
      const filteredItemsToAdd = await this.filterExistingCartItems(itemsToAdd);
      if (filteredItemsToAdd.length === 0) {
        this.isAddingGifts = false;
        return;
      }
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ items: filteredItemsToAdd })
      });
      if (response.ok) {
        await this.refreshCartUI();
      } else {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          errorData = await response.text();
        }
        let successCount = 0;
        for (const item of filteredItemsToAdd) {
          try {
            const individualResponse = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
              },
              body: JSON.stringify({ items: [item] })
            });
            if (individualResponse.ok) {
              successCount++;
            } else {
              const individualError = await individualResponse.text();
              console.error(`SubscriptionGifts: Failed to add individual item:`, item, individualError);
            }
          } catch (individualError) {
            console.error(`SubscriptionGifts: Exception adding individual item:`, item, individualError);
          }
        }
        if (successCount > 0) {
          await this.refreshCartUI();
        } else {
          console.error("SubscriptionGifts: Failed to add any gift products");
        }
      }
    } catch (error) {
      console.error("SubscriptionGifts: Error adding gift products to cart:", error);
    } finally {
      this.isAddingGifts = false;
    }
  }
  async getGiftProductsDetails(giftProductIds) {
    var _a;
    try {
      const query = `
        query GetGiftProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              handle
              availableForSale
              totalInventory
            }
          }
        }
      `;
      const response = await fetch(_SubscriptionGiftsHandler.STOREFRONT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": _SubscriptionGiftsHandler.ACCESS_TOKEN
        },
        body: JSON.stringify({
          query,
          variables: { ids: giftProductIds }
        })
      });
      if (!response.ok) {
        throw new Error(`Storefront API request failed: ${response.status}`);
      }
      const data = await response.json();
      if (data.errors) {
        console.error("SubscriptionGifts: Storefront API errors:", data.errors);
        return [];
      }
      return ((_a = data.data) == null ? void 0 : _a.nodes) || [];
    } catch (error) {
      console.error("SubscriptionGifts: Error fetching gift products details:", error);
      return [];
    }
  }
  async getGiftProductVariants(giftProducts) {
    var _a;
    const itemsToAdd = [];
    for (const product of giftProducts) {
      try {
        const productResponse = await fetch(`/products/${product.handle}.js`);
        if (!productResponse.ok) {
          console.warn(`SubscriptionGifts: Could not fetch product ${product.handle}`);
          continue;
        }
        const productData = await productResponse.json();
        const availableVariant = (_a = productData.variants) == null ? void 0 : _a.find((variant) => {
          return variant.available;
        });
        if (availableVariant) {
          itemsToAdd.push({
            id: availableVariant.id.toString(),
            quantity: 1
          });
        } else {
          console.warn(`SubscriptionGifts: No available variants found for ${product.handle}`);
          if (productData.variants && productData.variants.length > 0) {
            console.warn("SubscriptionGifts: All variants:", productData.variants.map((v) => ({
              id: v.id,
              available: v.available,
              inventory_quantity: v.inventory_quantity,
              inventory_management: v.inventory_management,
              inventory_policy: v.inventory_policy
            })));
          }
        }
      } catch (error) {
        console.error(`SubscriptionGifts: Error getting variants for ${product.handle}:`, error);
      }
    }
    return itemsToAdd;
  }
  extractProductIdFromGid(gid) {
    return gid.split("/").pop() || "";
  }
  async getCartData() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("SubscriptionGifts: Error fetching cart data:", error);
      return null;
    }
  }
  async filterExistingCartItems(itemsToAdd) {
    try {
      const cartData = await this.getCartData();
      if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
        return itemsToAdd;
      }
      const existingVariantIds = new Set(
        cartData.items.map((item) => item.id.toString())
      );
      const filteredItems = itemsToAdd.filter((item) => !existingVariantIds.has(item.id));
      return filteredItems;
    } catch (error) {
      console.error("SubscriptionGifts: Error filtering existing cart items:", error);
      return itemsToAdd;
    }
  }
  /**
   * Idempotently make sure every gift owed to the cart's subscription items is present.
   * Used by the checkout gift gate (brief section 9): called on checkout submit so a
   * buyer can never check out between the membership add and the gift attach.
   * Returns 'not-needed' (no subscription/gifts configured), 'complete' (gifts verified
   * in the cart response), or 'incomplete' (adds attempted but verification failed).
   */
  async ensureGiftsForCart() {
    const cart = await this.getCartData();
    const wantedVariants = await this.getWantedGiftVariants(cart);
    if (!wantedVariants.length) return "not-needed";
    const missing = await this.filterExistingCartItems(wantedVariants);
    if (!missing.length) return "complete";
    const subscriptionItems = ((cart == null ? void 0 : cart.items) || []).filter(
      (item) => {
        var _a, _b;
        return (_b = (_a = item == null ? void 0 : item.selling_plan_allocation) == null ? void 0 : _a.selling_plan) == null ? void 0 : _b.name;
      }
    );
    for (const item of subscriptionItems) {
      await this.processSubscriptionItem(item, cart);
    }
    const stillMissing = await this.filterExistingCartItems(wantedVariants);
    return stillMissing.length ? "incomplete" : "complete";
  }
  /**
   * Resolve every gift variant the given cart should contain (empty when the cart has no
   * gift-bearing subscription items). Metafield lookups are cached per page session, so
   * repeat calls are cheap; product availability is re-fetched each time.
   */
  async getWantedGiftVariants(cart) {
    const subscriptionItems = ((cart == null ? void 0 : cart.items) || []).filter(
      (item) => {
        var _a, _b;
        return (_b = (_a = item == null ? void 0 : item.selling_plan_allocation) == null ? void 0 : _a.selling_plan) == null ? void 0 : _b.name;
      }
    );
    const wantedVariants = [];
    for (const item of subscriptionItems) {
      const frequency = this.getFrequencyFromSellingPlan(
        item.selling_plan_allocation.selling_plan.name
      );
      if (!frequency) continue;
      const giftProductIds = await this.getGiftProductIds(
        item.handle,
        `subscription.${frequency}_gifts`
      );
      if (!giftProductIds || giftProductIds.length === 0) continue;
      const giftProducts = await this.getGiftProductsDetails(giftProductIds);
      wantedVariants.push(...await this.getGiftProductVariants(giftProducts));
    }
    return wantedVariants;
  }
  async refreshCartUI() {
    try {
      const cartData = await this.getCartData();
      document.dispatchEvent(new CustomEvent("theme:cart:refresh", {
        bubbles: true,
        detail: { cart: cartData }
      }));
      window.dispatchEvent(new CustomEvent("cart:updated", {
        detail: { cart: cartData }
      }));
    } catch (error) {
      console.error("SubscriptionGifts: Error refreshing cart UI:", error);
    }
  }
};
_SubscriptionGiftsHandler.STOREFRONT_URL = "https://for-them-store.myshopify.com/api/2025-04/graphql.json";
_SubscriptionGiftsHandler.ACCESS_TOKEN = "8325b00bc1524b6c8863c616e9226760";
let SubscriptionGiftsHandler = _SubscriptionGiftsHandler;
let subscriptionGiftsHandler = null;
function initializeSubscriptionGifts() {
  if (!subscriptionGiftsHandler) {
    subscriptionGiftsHandler = new SubscriptionGiftsHandler();
  }
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeSubscriptionGifts);
} else {
  initializeSubscriptionGifts();
}
async function ensureSubscriptionGifts() {
  initializeSubscriptionGifts();
  return subscriptionGiftsHandler.ensureGiftsForCart();
}
async function getWantedSubscriptionGiftVariants(cart) {
  initializeSubscriptionGifts();
  return subscriptionGiftsHandler.getWantedGiftVariants(cart);
}
if (typeof window !== "undefined") {
  window.dispatchSubscriptionAdded = SubscriptionGiftsHandler.dispatchSubscriptionAdded;
}
const GATE_TIMEOUT_MS = 8e3;
const WORKING_LABEL = "Adding your free magazine…";
let bypassNextSubmit = false;
let gateRunning = false;
let wantedCache = null;
let prewarmTimer = null;
function subscriptionKey(cart) {
  return ((cart == null ? void 0 : cart.items) || []).filter((item) => {
    var _a, _b;
    return (_b = (_a = item == null ? void 0 : item.selling_plan_allocation) == null ? void 0 : _a.selling_plan) == null ? void 0 : _b.name;
  }).map((item) => `${item.handle}:${item.selling_plan_allocation.selling_plan.name}`).sort().join("|");
}
async function fetchCart() {
  const response = await fetch("/cart.js");
  if (!response.ok) throw new Error(`cart fetch failed: ${response.status}`);
  return response.json();
}
async function prewarm() {
  try {
    const cart = await fetchCart();
    const key = subscriptionKey(cart);
    if (!key) {
      wantedCache = { key, variantIds: [] };
      return;
    }
    const wanted = await getWantedSubscriptionGiftVariants(cart);
    wantedCache = { key, variantIds: wanted.map((variant) => variant.id.toString()) };
  } catch (error) {
    console.warn("CheckoutGiftGate: prewarm failed", error);
  }
}
function schedulePrewarm(delayMs) {
  if (prewarmTimer) clearTimeout(prewarmTimer);
  prewarmTimer = setTimeout(() => void prewarm(), delayMs);
}
function timeoutAfter(ms) {
  return new Promise((resolve) => setTimeout(() => resolve("timeout"), ms));
}
async function runGate(form, button) {
  if (gateRunning) return;
  gateRunning = true;
  const originalLabel = button.innerHTML;
  const originalDisabled = button.disabled;
  let verified = false;
  try {
    const cart = await Promise.race([fetchCart(), timeoutAfter(2500)]);
    if (cart !== "timeout" && wantedCache && wantedCache.key === subscriptionKey(cart)) {
      const inCart = new Set((cart.items || []).map((item) => item.id.toString()));
      verified = wantedCache.variantIds.every((id) => inCart.has(id));
    }
  } catch (error) {
    console.warn("CheckoutGiftGate: fast path failed", error);
  }
  if (!verified) {
    button.disabled = true;
    button.innerHTML = WORKING_LABEL;
    let result = "error";
    try {
      result = await Promise.race([ensureSubscriptionGifts(), timeoutAfter(GATE_TIMEOUT_MS)]);
    } catch (error) {
      console.error("CheckoutGiftGate: ensure failed", error);
    }
    if (result !== "complete" && result !== "not-needed") {
      console.warn(`CheckoutGiftGate: proceeding without verified gift (${result})`);
    }
    button.innerHTML = originalLabel;
    button.disabled = originalDisabled;
  }
  gateRunning = false;
  bypassNextSubmit = true;
  try {
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit(button);
    } else {
      button.click();
    }
  } finally {
    setTimeout(() => {
      bypassNextSubmit = false;
    }, 1e3);
  }
}
function onCheckoutSubmit(event) {
  if (bypassNextSubmit) return;
  const submitter = event.submitter;
  if (!submitter || submitter.getAttribute("name") !== "checkout") return;
  const form = event.target;
  if (!form) return;
  event.preventDefault();
  event.stopPropagation();
  void runGate(form, submitter);
}
document.addEventListener("submit", onCheckoutSubmit, true);
schedulePrewarm(2500);
document.addEventListener("theme:cart:refresh", () => schedulePrewarm(500));
window.addEventListener("cart:updated", () => schedulePrewarm(500));
window.Shopify = window.Shopify || {};
window.Shopify.cart = {};
let activeOperations = /* @__PURE__ */ new Set();
let loadingStateInitialized = false;
window.Shopify.cart.loadingManager = {
  // Start a loading operation
  startLoading(operationId = "default") {
    activeOperations.add(operationId);
    this.updateLoadingState();
  },
  // End a loading operation
  endLoading(operationId = "default") {
    activeOperations.delete(operationId);
    this.updateLoadingState();
  },
  // Check if any operations are active
  isLoading() {
    return activeOperations.size > 0;
  },
  // Force end all operations (cleanup)
  endAllLoading() {
    activeOperations.clear();
    this.updateLoadingState();
  },
  // Update the UI loading state
  updateLoadingState() {
    const isLoading = this.isLoading();
    this.updateCartDrawerLoadingState(isLoading);
    this.updateCheckoutButtonsState(isLoading);
    this.updateCartContainersLoadingState(isLoading);
    window.dispatchEvent(new CustomEvent("cart:loading-state-changed", {
      detail: { isLoading, activeOperations: Array.from(activeOperations) }
    }));
  },
  // Update cart drawer with loading overlay
  updateCartDrawerLoadingState(isLoading) {
    const cartDrawers = document.querySelectorAll("#cart-drawer, [data-cart-drawer]");
    cartDrawers.forEach((drawer) => {
      const drawerElement = drawer;
      if (isLoading) {
        drawerElement.classList.add("is-cart-loading");
        this.createCartLoadingOverlay(drawerElement);
      } else {
        drawerElement.classList.remove("is-cart-loading");
        this.hideCartLoadingOverlay(drawerElement);
      }
    });
  },
  // Create loading overlay for cart drawer
  createCartLoadingOverlay(cartDrawer) {
    let overlay = cartDrawer.querySelector(".cart-loading-overlay");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.className = "cart-loading-overlay";
      overlay.innerHTML = `<div></div>
      `;
      cartDrawer.insertBefore(overlay, cartDrawer.firstChild);
    }
    overlay.style.display = "block";
    overlay.offsetHeight;
    overlay.classList.add("is-visible");
  },
  // Hide loading overlay for cart drawer
  hideCartLoadingOverlay(cartDrawer) {
    const overlay = cartDrawer.querySelector(".cart-loading-overlay");
    if (overlay) {
      overlay.classList.remove("is-visible");
      setTimeout(() => {
        if (!this.isLoading()) {
          overlay.style.display = "none";
        }
      }, 300);
    }
  },
  // Update checkout buttons state
  updateCheckoutButtonsState(isLoading) {
    const checkoutButtons = document.querySelectorAll(
      '[data-cart-checkout-button] button[type="submit"], [data-cart-checkout-buttons] button[type="submit"], .cart__checkout, .cart__buttons__fieldset'
    );
    checkoutButtons.forEach((element) => {
      const btn = element;
      if (isLoading) {
        if (btn.tagName === "BUTTON") {
          btn.disabled = true;
        } else if (btn.tagName === "FIELDSET") {
          btn.disabled = true;
        }
        btn.classList.add("is-loading");
        if (btn.tagName === "BUTTON" && !btn.querySelector(".btn__loader")) {
          this.addButtonLoader(btn);
        }
      } else {
        if (btn.tagName === "BUTTON") {
          btn.disabled = false;
        } else if (btn.tagName === "FIELDSET") {
          btn.disabled = false;
        }
        btn.classList.remove("is-loading");
      }
    });
  },
  // Add button loader element
  addButtonLoader(button) {
    if (button.querySelector(".btn__loader")) return;
    const loader = document.createElement("div");
    loader.className = "btn__loader";
    loader.innerHTML = `
      <svg class="svg-loader" width="20" height="20" viewBox="0 0 32 32">
        <circle cx="16" cy="16" r="14" stroke-dasharray="88" stroke-dashoffset="88"></circle>
        <circle cx="16" cy="16" r="14" stroke-dasharray="88" stroke-dashoffset="88"></circle>
      </svg>
    `;
    button.appendChild(loader);
  },
  // Update cart containers loading state
  updateCartContainersLoadingState(isLoading) {
    const cartContainers = document.querySelectorAll(
      "[data-api-content], .cart__items, [data-cart-items], .drawer__items"
    );
    cartContainers.forEach((container) => {
      const containerElement = container;
      if (isLoading) {
        containerElement.classList.add("is-loading");
      } else {
        containerElement.classList.remove("is-loading");
      }
    });
  }
};
window.Shopify.cart.initializeLoadingStyles = () => {
  if (loadingStateInitialized) return;
  const style = document.createElement("style");
  style.id = "cart-loading-styles";
  style.textContent = `
    .cart-loading-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10001;
      display: none;
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    
    .cart-loading-overlay.is-visible {
      opacity: 1;
    }
    
    .cart-loading-overlay__backdrop {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(2px);
    }
    
    .cart-loading-overlay__spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
    }
    
    .cart-loading-overlay__spinner-inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 24px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    }
    
    .cart-loading-overlay__text {
      font-size: 14px;
      color: var(--text-dark, #333);
      font-weight: 500;
    }
    
    .cart-loading-overlay .svg-loader circle ~ circle {
      animation-play-state: running;
    }
    
    /* Ensure loading overlay is positioned relative to cart drawer */
    #cart-drawer.is-cart-loading {
      position: relative;
    }
    
    #cart-drawer .cart-loading-overlay {
      position: absolute;
      z-index: 100;
    }
    
    /* Loading state for cart containers */
    .drawer__items.is-loading,
    .cart__items.is-loading,
    [data-cart-items].is-loading {
      opacity: 0.7;
      pointer-events: none;
    }
    
    /* Disable interactions during loading */
    .is-cart-loading .drawer__inner {
      pointer-events: none;
    }
    
    .is-cart-loading .cart-loading-overlay {
      pointer-events: auto;
    }
    
    /* Additional checkout button loading states */
    .cart__buttons__fieldset.is-loading {
      opacity: 0.7;
      pointer-events: none;
    }
    
    .cart__checkout.is-loading {
      position: relative;
    }
  `;
  document.head.appendChild(style);
  loadingStateInitialized = true;
};
document.addEventListener("DOMContentLoaded", () => {
  window.Shopify.cart.initializeLoadingStyles();
});
window.Shopify.cart.add = ({ items, onAdded }) => {
  if (!items) {
    console.error("Product data is required to add an item to the cart.");
    return;
  }
  const operationId = `add-to-cart-${Date.now()}`;
  window.Shopify.cart.loadingManager.startLoading(operationId);
  fetch(`/cart/add.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ items })
  }).then((response) => {
    return response.json();
  }).then((data) => {
    window.dispatchEvent(
      new CustomEvent("cart:added", { detail: { item: data } })
    );
    if (onAdded && typeof onAdded === "function") {
      onAdded(data);
    }
  }).catch((error) => console.error(error)).finally(() => {
    window.Shopify.cart.loadingManager.endLoading(operationId);
  });
};
window.Shopify.cart.removeItem = ({ id, onRemove }) => {
  const operationId = `remove-item-${Date.now()}`;
  window.Shopify.cart.loadingManager.startLoading(operationId);
  fetch(`/cart/change.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ id, quantity: 0 })
  }).then((response) => {
    return response.json();
  }).then((data) => {
    const event = new CustomEvent("cart:removed", {
      detail: { id, cart: data }
    });
    window.dispatchEvent(event);
    if (onRemove && typeof onRemove === "function") {
      onRemove(data);
    }
  }).catch((error) => console.error(error)).finally(() => {
    window.Shopify.cart.loadingManager.endLoading(operationId);
  });
};
window.Shopify.cart.getCart = (callback) => {
  return fetch("/cart.js", {
    method: "GET",
    headers: {
      "Content-Type": "application/json"
    }
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Couldn't fetch cart! Status: ${response.status}`);
    }
    return response.json();
  }).then((data) => {
    const event = new CustomEvent("cart:fetched", { detail: data });
    window.dispatchEvent(event);
    if (callback && typeof callback === "function") {
      callback(data);
    }
    return data;
  }).catch((error) => {
    console.error(error);
    throw error;
  });
};
window.Shopify.cart.updateItem = ({
  id,
  quantity,
  sellingPlanId,
  properties,
  onUpdate
}) => {
  const operationId = `update-item-${Date.now()}`;
  window.Shopify.cart.loadingManager.startLoading(operationId);
  let payload = {
    id
  };
  if (quantity !== void 0 && quantity !== null) {
    payload["quantity"] = quantity;
  }
  if (sellingPlanId !== void 0) {
    if (sellingPlanId && sellingPlanId !== "Select" && sellingPlanId !== "") {
      payload["selling_plan"] = sellingPlanId;
    } else {
      payload["selling_plan"] = "";
    }
  }
  if (properties) {
    payload["properties"] = properties;
  }
  fetch("/cart/change.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }).then((data) => {
    const event = new CustomEvent("cart:updated", {
      detail: { payload, cart: data }
    });
    window.dispatchEvent(event);
    if (onUpdate && typeof onUpdate === "function") {
      onUpdate(data);
    }
  }).catch((error) => {
    if (onUpdate && typeof onUpdate === "function") {
      onUpdate({ errors: error.message });
    }
  }).finally(() => {
    window.Shopify.cart.loadingManager.endLoading(operationId);
  });
};
window.Shopify.cart.getSellingPlans = (handles = []) => {
  return handles.map(
    (handle) => fetch(`/products/${handle}.js`).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${handle}`);
      }
      return response.json();
    }).then((product) => {
      if (product && product.selling_plan_groups) {
        return {
          productId: product.id,
          sellingPlans: product.selling_plan_groups
        };
      }
      return null;
    }).catch((error) => {
      return null;
    })
  );
};
let validationInProgress = false;
let validationDebounceTimer = null;
let lastValidationCart = null;
const productDataCache = /* @__PURE__ */ new Map();
window.Shopify.cart.getCachedProductData = async (productId) => {
  var _a;
  const cacheKey = productId.toString();
  if (productDataCache.has(cacheKey)) {
    return productDataCache.get(cacheKey);
  }
  try {
    let productHandle = null;
    const currentCart = await window.Shopify.cart.getCart();
    const cartItem = (_a = currentCart == null ? void 0 : currentCart.items) == null ? void 0 : _a.find(
      (item) => {
        var _a2;
        return ((_a2 = item.product_id) == null ? void 0 : _a2.toString()) === productId.toString();
      }
    );
    if (cartItem == null ? void 0 : cartItem.handle) {
      productHandle = cartItem.handle;
    } else {
      console.warn(`Could not find product handle for ID ${productId}`);
      return null;
    }
    const productResponse = await fetch(`/products/${productHandle}.js`);
    if (!productResponse.ok) {
      console.warn(`Failed to fetch product data for handle ${productHandle}`);
      return null;
    }
    const productData = await productResponse.json();
    productDataCache.set(cacheKey, productData);
    setTimeout(() => {
      productDataCache.delete(cacheKey);
    }, 5 * 60 * 1e3);
    return productData;
  } catch (error) {
    console.error(`Error fetching product data for ${productId}:`, error);
    return null;
  }
};
const debouncedValidation = (cartData, delay = 500) => {
  var _a;
  if (validationDebounceTimer !== null) {
    clearTimeout(validationDebounceTimer);
  }
  const cartHash = cartData ? JSON.stringify({
    items: ((_a = cartData.items) == null ? void 0 : _a.map((item) => ({
      key: item.key,
      variant_id: item.variant_id,
      quantity: item.quantity
    }))) || [],
    total_price: cartData.total_price
  }) : "empty";
  if (cartHash === lastValidationCart) {
    return;
  }
  validationDebounceTimer = setTimeout(() => {
    window.Shopify.cart.validateMembersOnlyProducts(cartData);
    validationDebounceTimer = null;
  }, delay);
};
window.Shopify.cart.validateMembersOnlyProducts = async (cartData = null) => {
  var _a, _b, _c;
  if (validationInProgress) {
    return;
  }
  validationInProgress = true;
  const operationId = `validate-members-only-${Date.now()}`;
  window.Shopify.cart.loadingManager.startLoading(operationId);
  try {
    if (!cartData) {
      cartData = await window.Shopify.cart.getCart();
    }
    const cartHash = cartData ? JSON.stringify({
      items: ((_a = cartData.items) == null ? void 0 : _a.map((item) => ({
        key: item.key,
        variant_id: item.variant_id,
        quantity: item.quantity
      }))) || [],
      total_price: cartData.total_price
    }) : "empty";
    if (!((_b = cartData == null ? void 0 : cartData.items) == null ? void 0 : _b.length)) {
      lastValidationCart = cartHash;
      return;
    }
    const hasQualifyingSubscriptionInCart = window.Shopify.cart.hasQualifyingSubscription(cartData);
    if (hasQualifyingSubscriptionInCart) {
      lastValidationCart = cartHash;
      return;
    }
    let hasActiveMembership = false;
    const isLoggedIn = document.body.classList.contains("customer-logged-in") || window.customer && window.customer.id;
    if (isLoggedIn) {
      try {
        const membershipResult = await membershipService.getCurrentMembershipStatus();
        console.log({ membershipResult });
        if (membershipResult.memberships && membershipResult.memberships.length > 0) {
          const membershipName = membershipResult.memberships[0].name.toLowerCase();
          hasActiveMembership = membershipName === FORTHEM_TIER_NAME_ALLACCESS.toLowerCase() || membershipName === FORTHEM_TIER_NAME_SUPPORT.toLowerCase();
        }
      } catch (error) {
        console.warn("Error checking customer membership status:", error);
      }
    }
    if (hasActiveMembership) {
      lastValidationCart = cartHash;
      return;
    }
    const membersOnlyItems = [];
    const productDataPromises = [];
    for (const item of cartData.items) {
      if (!item.product_id) continue;
      const productDataPromise = window.Shopify.cart.getCachedProductData(item.product_id).then((productData) => ({ item, productData }));
      productDataPromises.push(productDataPromise);
    }
    const productResults = await Promise.all(productDataPromises);
    for (const { item, productData } of productResults) {
      if (!productData) continue;
      const productTags = productData.tags || [];
      const isMembersOnly = productTags.some((tag) => tag.toLowerCase() === "members-only-product");
      if (isMembersOnly) {
        membersOnlyItems.push(item);
      }
    }
    if (membersOnlyItems.length === 0) {
      lastValidationCart = cartHash;
      return;
    }
    const removePromises = membersOnlyItems.map((item) => {
      return new Promise((resolve, reject) => {
        window.Shopify.cart.removeItem({
          id: item.key,
          onRemove: (result) => {
            if (result.errors) {
              reject(new Error(`Failed to remove item ${item.key}: ${result.errors}`));
            } else {
              resolve(result);
            }
          }
        });
      });
    });
    await Promise.all(removePromises);
    window.Shopify.cart.showMembersOnlyNotification(membersOnlyItems);
    await window.Shopify.cart.refreshCart();
    const updatedCart = await window.Shopify.cart.getCart();
    lastValidationCart = JSON.stringify({
      items: ((_c = updatedCart.items) == null ? void 0 : _c.map((item) => ({
        key: item.key,
        variant_id: item.variant_id,
        quantity: item.quantity
      }))) || [],
      total_price: updatedCart.total_price
    });
    window.dispatchEvent(new CustomEvent("cart:members-only-cleaned", {
      detail: {
        removedItems: membersOnlyItems.length,
        reason: "no-qualifying-subscription"
      }
    }));
  } catch (error) {
    console.error("Members-only validation error:", error);
    lastValidationCart = null;
  } finally {
    window.Shopify.cart.loadingManager.endLoading(operationId);
    validationInProgress = false;
  }
};
window.Shopify.cart.showMembersOnlyNotification = (removedItems) => {
  var _a;
  const itemCount = removedItems.length;
  const itemNames = removedItems.map((item) => item.product_title).join(", ");
  const message = itemCount === 1 ? `The item "${itemNames}" was removed from your cart because it requires a membership subscription.` : `${itemCount} items (${itemNames}) were removed from your cart because they require a membership subscription.`;
  const errorContainers = document.querySelectorAll("[data-cart-error-message]");
  if (errorContainers.length > 0) {
    const textureUrls = ((_a = window.cartMembershipUpsellSettings) == null ? void 0 : _a.textureUrls) || {};
    const notificationHTML = `
      <div class="rs-relative rs-mb-4 rs-mx-[15px] rs-overflow-hidden rs-rounded-lg members-only-notification" >
        <!-- Desktop texture -->
        ${(textureUrls == null ? void 0 : textureUrls.blueTextureDesktop) ? `
          <div 
            class="rs-top-0 rs-left-0 rs-right-0 rs-h-[55px] rs-overflow-hidden rs-z-0 rs-hidden md:rs-block"
            style="
              background-image: url(${textureUrls.blueTextureDesktop});
              background-position: top;
              background-repeat: no-repeat;
              background-size: 100%;
            "
          ></div>
        ` : ""}
        
        <!-- Mobile texture -->
        ${(textureUrls == null ? void 0 : textureUrls.blueTextureMobile) ? `
          <div 
            class="rs-top-0 rs-left-0 rs-right-0 rs-h-[55px] rs-overflow-hidden rs-z-0 rs-block md:rs-hidden"
            style="
              background-image: url(${textureUrls.blueTextureMobile});
              background-position: top;
              background-repeat: no-repeat;
              background-size: 100%;
            "
          ></div>
        ` : ""}
        
                <!-- Content -->
        <div class="rs-relative rs-z-10 rs-px-4 rs-pb-4 rs-text-white rs-bg-[#0069E4]">
          <div class="rs-flex rs-flex-col rs-gap-4">
            <div class="rs-flex rs-items-start rs-justify-between">
              <div class="rs-flex-1">       
                <h3 class="rs-text-[20px] rs-font-bold !rs-leading-none rs-mb-3">Oh No!</h3>
                <p class="rs-font-tertiary rs-text-base !rs-leading-none rs-mb-0 rs-opacity-90">That was a Member-Only Product. Join AF+ All Access to continue</p>
              </div>
            </div>
            <button
             class="rs-w-full rs-py-3 rs-px-6 rs-rounded-full rs-font-bold rs-text-sm rs-uppercase rs-transition-colors rs-bg-black rs-text-white hover:rs-bg-gray-900"
             onclick="window.Shopify.cart.readdRemovedMembersOnlyProducts(this)"
             data-removed-items='${JSON.stringify(removedItems).replace(/'/g, "&#39;")}'
            >
             Add
            </button>
          </div>
        </div>
    `;
    const cartMembershipUpsells = document.querySelectorAll("cart-membership-upsell, .cart-membership-upsell");
    cartMembershipUpsells.forEach((upsell) => {
      upsell.style.display = "none";
    });
    errorContainers.forEach((container) => {
      const existingNotifications = container.querySelectorAll(".members-only-notification");
      existingNotifications.forEach((notification2) => notification2.remove());
      container.innerHTML = notificationHTML;
      const notification = container.querySelector(".members-only-notification");
      if (notification) {
        setTimeout(() => {
          if (notification.parentElement) {
            notification.remove();
            cartMembershipUpsells.forEach((upsell) => {
              upsell.style.display = "";
            });
          }
        }, 1e4);
      }
    });
  } else {
    if (window.theme && window.theme.toast && typeof window.theme.toast.show === "function") {
      window.theme.toast.show({
        message: message + " Add a Support or All Access membership to access these products.",
        type: "warning",
        duration: 1e4
      });
    } else if (window.theme && window.theme.alert && typeof window.theme.alert === "function") {
      window.theme.alert(message + " Add a Support or All Access membership to access these products.");
    } else {
      const notification = document.createElement("div");
      notification.innerHTML = `
        <div style="
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          background-color: #0069E4;
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          font-family: system-ui, -apple-system, sans-serif;
          font-size: 14px;
          z-index: 10000;
          max-width: 90vw;
          text-align: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        ">
          <strong>Cart Updated:</strong> ${message}<br>
          <small>Add a Support or All Access membership to access these products.</small>
          <button onclick="this.parentElement.parentElement.remove()" style="
            background: none;
            border: none;
            color: white;
            font-size: 18px;
            position: absolute;
            top: 8px;
            right: 12px;
            cursor: pointer;
          ">&times;</button>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 1e4);
    }
  }
};
window.Shopify.cart.readdRemovedMembersOnlyProducts = async (buttonElement) => {
  const operationId = `readd-members-only-${Date.now()}`;
  window.Shopify.cart.loadingManager.startLoading(operationId);
  try {
    const removedItemsJson = buttonElement.getAttribute("data-removed-items");
    if (!removedItemsJson) {
      console.error("No removed items data found");
      return;
    }
    const removedItems = JSON.parse(removedItemsJson);
    const itemsToAdd = removedItems.map((item) => {
      const cartItem = {
        id: item.variant_id.toString(),
        quantity: item.quantity || 1
      };
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        cartItem.selling_plan = item.selling_plan_allocation.selling_plan.id;
      }
      return cartItem;
    });
    const membershipItem = {
      id: "44792925257894",
      // AF+ All Access variant ID
      quantity: 1,
      selling_plan: "1580236966"
      // AF+ All Access selling plan ID
    };
    itemsToAdd.push(membershipItem);
    const originalText = buttonElement.textContent;
    buttonElement.textContent = "Adding...";
    buttonElement.disabled = true;
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({ items: itemsToAdd })
    });
    if (response.ok) {
      const notification = buttonElement.closest(".members-only-notification");
      if (notification) {
        notification.remove();
      }
      const cartMembershipUpsells = document.querySelectorAll("cart-membership-upsell, .cart-membership-upsell");
      cartMembershipUpsells.forEach((upsell) => {
        upsell.style.display = "";
      });
      await window.Shopify.cart.refreshCart();
      document.dispatchEvent(new CustomEvent("theme:cart:refresh", {
        bubbles: true
      }));
    } else {
      const errorData = await response.json();
      console.error("Error re-adding products to cart:", errorData);
      buttonElement.textContent = "Error - Try Again";
      buttonElement.disabled = false;
      setTimeout(() => {
        buttonElement.textContent = originalText;
      }, 3e3);
    }
  } catch (error) {
    console.error("Error re-adding members-only products:", error);
    buttonElement.textContent = "Error - Try Again";
    buttonElement.disabled = false;
    setTimeout(() => {
      buttonElement.textContent = "Add";
    }, 3e3);
  } finally {
    window.Shopify.cart.loadingManager.endLoading(operationId);
  }
};
window.Shopify.cart.refreshCart = async () => {
  const operationId = `refresh-cart-${Date.now()}`;
  window.Shopify.cart.loadingManager.startLoading(operationId);
  try {
    const freshCart = await window.Shopify.cart.getCart();
    document.dispatchEvent(new CustomEvent("theme:cart:refresh", {
      bubbles: true,
      detail: { cart: freshCart }
    }));
    window.dispatchEvent(new CustomEvent("cart:updated", {
      detail: { cart: freshCart }
    }));
    document.dispatchEvent(new CustomEvent("theme:cart:change", {
      bubbles: true,
      detail: { cart: freshCart }
    }));
    window.dispatchEvent(new CustomEvent("cart:refreshed", {
      detail: { cart: freshCart }
    }));
    window.dispatchEvent(new CustomEvent("cart:refresh"));
    const cartContainers = document.querySelectorAll("[data-api-content], .cart__items, [data-cart-items]");
    cartContainers.forEach((container) => {
      if (container && typeof container.dispatchEvent === "function") {
        container.dispatchEvent(new CustomEvent("cart:update", { bubbles: true }));
      }
    });
    return freshCart;
  } catch (error) {
  } finally {
    window.Shopify.cart.loadingManager.endLoading(operationId);
  }
};
window.Shopify.cart.hasQualifyingSubscription = (cart) => {
  if (!cart || !cart.items) return false;
  const membershipVariantIds = ["44792925257894", "46214081347750"];
  const membershipHandles = ["af-membership", "support-the-community", "af-plus-toki"];
  return cart.items.some((item) => {
    var _a;
    if (!item.selling_plan_allocation) return false;
    if (membershipVariantIds.includes((_a = item.variant_id) == null ? void 0 : _a.toString())) {
      return true;
    }
    if (membershipHandles.includes(item.handle)) {
      return true;
    }
    return false;
  });
};
document.addEventListener("DOMContentLoaded", () => {
  window.addEventListener("cart:added", (event) => {
    setTimeout(() => {
      var _a;
      const cartData = ((_a = event.detail) == null ? void 0 : _a.cart) || null;
      debouncedValidation(cartData);
    }, 100);
  });
  window.addEventListener("cart:updated", (event) => {
    setTimeout(() => {
      var _a;
      const cartData = ((_a = event.detail) == null ? void 0 : _a.cart) || null;
      debouncedValidation(cartData);
    }, 100);
  });
  window.addEventListener("cart:removed", (event) => {
    setTimeout(() => {
      var _a;
      const cartData = ((_a = event.detail) == null ? void 0 : _a.cart) || null;
      debouncedValidation(cartData);
    }, 100);
  });
  window.addEventListener("cart:fetched", (event) => {
    if (!validationInProgress) {
      setTimeout(() => {
        const cartData = event.detail || null;
        debouncedValidation(cartData);
      }, 100);
    }
  });
  document.addEventListener("theme:cart:refresh", (event) => {
    setTimeout(() => {
      var _a;
      const cartData = ((_a = event.detail) == null ? void 0 : _a.cart) || null;
      debouncedValidation(cartData);
    }, 100);
  });
  document.addEventListener("theme:cart:change", (event) => {
    setTimeout(() => {
      var _a;
      const cartData = ((_a = event.detail) == null ? void 0 : _a.cart) || null;
      debouncedValidation(cartData);
    }, 100);
  });
});
window.Shopify.cart.validateMembershipTiers = async (cartData = null) => {
  try {
    const { membershipTierValidator } = await __vitePreload(async () => {
      const { membershipTierValidator: membershipTierValidator2 } = await import("main/membership-tier-validator");
      return { membershipTierValidator: membershipTierValidator2 };
    }, true ? [] : void 0, import.meta.url);
    const validation = await membershipTierValidator.validateCart();
    if (!validation.isValid) {
      console.warn("Membership tier validation errors:", validation.errors);
      const event = new CustomEvent("cart:membership-tier-validation-error", {
        detail: { errors: validation.errors }
      });
      window.dispatchEvent(event);
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error validating membership tiers:", error);
    return false;
  }
};
document.addEventListener("DOMContentLoaded", () => {
  ["theme:cart:add", "theme:product:add", "theme:cart:change"].forEach((eventType) => {
    document.addEventListener(eventType, (e) => {
      console.log("[eventtype]", e);
      setTimeout(() => window.Shopify.cart.validateMembershipTiers(), 500);
    });
  });
});
